import React, { useEffect, useMemo } from "react";
import Layout from "@/components/Layout";
import { Link } from "react-router-dom";
import { usePublicSettings } from "@/lib/publicSettings";
import { ArrowRight } from "lucide-react";
import DOMPurify from "dompurify";

/**
 * LegalPageShell — shared frame for Privacy / Terms / Refund pages.
 * Renders the built-in `defaultBody` unless an admin has overridden the
 * content in Settings → legal.{slug}.html (rich HTML string). This gives us
 * an easy on-ramp for future admin CMS editing without shipping a full
 * legal-content management module today.
 *
 * The default body should be treated as a REVIEW TEMPLATE — not final
 * legal advice. Have the copy vetted by an attorney before public launch.
 */
const LegalPageShell = ({ slug, title, effectiveDate, summary, defaultBody, contactEmail }) => {
  const { settings } = usePublicSettings();
  const override = settings?.legal?.[slug]?.html;
  const overrideDate = settings?.legal?.[slug]?.effective_date;
  const overrideSummary = settings?.legal?.[slug]?.summary;

  useEffect(() => {
    document.title = `${title} — Dream Home Plans`;
    setMeta("description", summary);
    setLink("canonical", `${window.location.origin}/${slug}`);
    // setMeta / setLink are stable module-level helpers defined below —
    // safe to omit from deps. `title`/`summary`/`slug` capture every real
    // change we care about.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, summary, slug]);

  const dateStr = overrideDate || effectiveDate;
  const summaryStr = overrideSummary || summary;

  // Sanitize both admin-supplied overrides AND the shipped default body
  // through DOMPurify before rendering. Even though defaultBody is trusted
  // (it lives in our repo), running it through DOMPurify gives a single
  // consistent output path and closes off the injection surface if an
  // admin ever pastes hostile HTML into Settings → Legal.
  const cleanBody = useMemo(
    () => DOMPurify.sanitize(override || defaultBody || "", { USE_PROFILES: { html: true } }),
    [override, defaultBody],
  );

  return (
    <Layout>
      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-16 lg:py-24" data-testid={`legal-${slug}-page`}>
        <div className="overline text-accent" data-testid={`legal-${slug}-eyebrow`}>Legal</div>
        <h1 className="font-display text-4xl lg:text-6xl mt-4 leading-[0.95] tracking-tighter" data-testid={`legal-${slug}-title`}>
          {title}
        </h1>
        <div className="mt-6 text-sm text-muted-foreground" data-testid={`legal-${slug}-effective`}>
          Effective date: <span className="text-foreground">{dateStr}</span>
        </div>
        <p className="mt-6 text-lg text-muted-foreground max-w-3xl leading-relaxed" data-testid={`legal-${slug}-summary`}>
          {summaryStr}
        </p>

        <div className="mt-4 inline-flex items-start gap-3 bg-amber-50 border border-amber-200 text-amber-900 text-xs px-4 py-3 rounded-none" data-testid={`legal-${slug}-review-notice`}>
          <span className="font-semibold uppercase tracking-widest">Draft template</span>
          <span>This page is a review template written for a house-plan marketplace and is not legal advice. Have it reviewed by qualified counsel before public launch.</span>
        </div>

        <article
          className="legal-body prose prose-neutral max-w-none mt-12 text-[15px] leading-relaxed
                     prose-headings:font-display prose-headings:tracking-tight prose-headings:text-foreground
                     prose-h2:text-3xl prose-h2:mt-14 prose-h2:mb-4
                     prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
                     prose-p:text-foreground/85 prose-p:my-4
                     prose-ul:my-4 prose-li:my-1.5 prose-li:marker:text-accent
                     prose-strong:text-foreground prose-a:text-accent prose-a:no-underline hover:prose-a:underline"
          data-testid={`legal-${slug}-body`}
          dangerouslySetInnerHTML={{ __html: cleanBody }}
        />

        <div className="mt-16 pt-8 border-t border-border flex flex-wrap items-center justify-between gap-4">
          <div className="text-sm text-muted-foreground">
            Questions about this policy? Email us at{" "}
            <a href={`mailto:${contactEmail}`} className="text-accent hover:underline" data-testid={`legal-${slug}-contact-email`}>
              {contactEmail}
            </a>
          </div>
          <Link to="/contact" data-testid={`legal-${slug}-contact-cta`} className="inline-flex items-center gap-2 text-foreground hover:text-accent overline">
            Contact us <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </Layout>
  );
};

function setMeta(name, content) {
  if (!content) return;
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default LegalPageShell;
